import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, Col, Radio as RadioElement, Row, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { HRAInformation } from '../../types';
import Collapse from '../../common/components/dataEntry/components/Collapse';
import CollapsePanel from '../../common/components/dataEntry/components/CollapsePanel';
import Radio from '../../common/components/dataEntry/formik/FormikRadioField';
import FormikInputNumberField from '../../common/components/dataEntry/formik/FormikInputNumberField';
import Label from '../../common/components/dataEntry/components/Label';

export const HRADefaultValues = {
  monthlyAllowance: 0,
  isAllowanceForYou: '',
  singleAllowance: 0,
};

const formValidator = Yup.object({
  monthlyAllowance: Yup.number()
    .required('Required')
    .typeError('Invalid input'),
  isAllowanceForYou: Yup.string().required('Required'),
});

interface Step2Props {
  hraInfoInitialvalues: HRAInformation | null;
  onStep2Complete: (values: HRAInformation) => void;
  currentStep: number;
  displayResult: (value: boolean) => void;
  lowestCostPlan: number;
  householdIncome: number | null;
  showAffordable: boolean;
  showUnaffordable: boolean;
  clearStep: () => void;
  defaultKey: number;
  activeAccordion: number;
  handleCollapse: () => void;
}

const Step2: React.FC<Step2Props> = ({
  onStep2Complete,
  hraInfoInitialvalues,
  displayResult,
  currentStep,
  householdIncome,
  lowestCostPlan,
  showAffordable,
  showUnaffordable,
  clearStep,
  defaultKey,
  activeAccordion,
  handleCollapse,
}) => {
  const [showPositiveMsg, setShowPositiveMsg] = useState(false);
  const [showSingleAllowance, setShowSingleAllowance] = useState(false);

  if (currentStep !== 1) return null;

  const formInitialValues: HRAInformation =
    hraInfoInitialvalues ?? HRADefaultValues;

  const onSubmit = async (values: HRAInformation) => {
    const afforadable: boolean = calculateAffordability(values);
    displayResult(afforadable);
    onStep2Complete(values);
  };

  const calculateAffordability = (values: HRAInformation) => {
    return (
      (Number(householdIncome) * 0.0912) / 12 >
      lowestCostPlan -
        (showSingleAllowance ? values.singleAllowance : values.monthlyAllowance)
    );
  };
  return (
    <div>
      <Collapse activeKey={activeAccordion} accordion onChange={handleCollapse}>
        <CollapsePanel header="HRA Information" key={defaultKey}>
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator}
            initialValues={formInitialValues}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit} autoComplete="off">
                <Label className="formik-field__input-label no-select hand-on-hover ml-16 mb-8 black">
                  What is your monthly HRA allowance?
                </Label>
                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={4} xl={3}>
                    <FormikInputNumberField
                      onChange={clearStep}
                      name="monthlyAllowance"
                      formatter={(value) =>
                        `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      decimalSeparator="."
                      precision={2}
                    />
                  </Col>
                </Row>

                <Label className="formik-field__input-label no-select hand-on-hover mb-8 ml-16 black">
                  Is this allowance amount for a single person only?
                </Label>

                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={3} xl={3}>
                    <Radio
                      className="radio"
                      value={formik.values.isAllowanceForYou}
                      name="isAllowanceForYou"
                      onRadioChange={(e) => {
                        if (e.target.value === '1') {
                          setShowPositiveMsg(true);
                          setShowSingleAllowance(false);
                        } else if (e.target.value === '2' || '3') {
                          setShowPositiveMsg(false);
                          setShowSingleAllowance(true);
                        }
                        clearStep();
                      }}
                    >
                      <RadioElement value={'1'}>
                        <p>Yes </p>
                      </RadioElement>
                      <RadioElement value={'2'}>
                        <p>No </p>
                      </RadioElement>
                      <RadioElement value={'3'}>
                        <p>I don't know </p>
                      </RadioElement>
                    </Radio>
                  </Col>
                </Row>
                {showPositiveMsg && (
                  <Row className="ml-16">
                    <Alert
                      message=""
                      className="noborder"
                      style={{ marginBottom: 20 }}
                      showIcon={false}
                      type="success"
                      description="Excellent! Affordability is calculated based on the single person (self only) ICHRA allowance."
                    ></Alert>
                  </Row>
                )}
                {showSingleAllowance && (
                  <div>
                    <Label className="formik-field__input-label no-select hand-on-hover ml-16 mb-8 black">
                      {
                        <div>
                          What is the self-only (single person) allowance amount
                          offered under your ICHRA plan?
                          <Tooltip
                            placement="right"
                            title="For PeopleKeep users, you can find the single (self-only)
                        allowance amount offered under your employer's ICHRA benefit in
                        your welcome notice email. The email subject line is: 'Learn more
                        about your PeopleKeep benefit'. For other users, you can contact
                        your plan administrator to clarify if your monthly ICHRA allowance
                        varies by family status."
                          >
                            <ExclamationCircleOutlined className="ml-8" />
                          </Tooltip>
                        </div>
                      }
                    </Label>
                    <Row className="ml-16">
                      <Col xs={12} sm={6} lg={3} xl={3}>
                        <FormikInputNumberField
                          onChange={clearStep}
                          formatter={(value) =>
                            `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          decimalSeparator="."
                          precision={2}
                          name="singleAllowance"
                        />
                      </Col>
                    </Row>
                  </div>
                )}
                <Row className="ml-16">
                  <Col xs={12} sm={6} lg={4} xl={3}>
                    <Button
                      className="mb-16 button__large"
                      type="primary"
                      disabled={!formValidator.isValidSync(formik.values)}
                      htmlType="submit"
                    >
                      Calculate affordability
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        </CollapsePanel>
      </Collapse>
      {showAffordable && (
        <Alert
          className="mt-32 mb-32 font-large-text"
          type="success"
          showIcon={false}
          message=""
          description={
            <div>
              <h3>YES</h3>
              <p>
                Your allowance is affordable based on the your income, the
                lowest cost silver plan available to you of ${lowestCostPlan}{' '}
                and your monthly allowance of $
                {showSingleAllowance
                  ? formInitialValues?.singleAllowance
                  : formInitialValues?.monthlyAllowance}
                .<br />
                <br />
                What does this mean?
                <br />
                <br />
                <i>
                  - To be considered affordable by the IRS, the cost of health
                  insurance for an employee must not be more than 9.12% of the
                  employee’s income.
                </i>
                <br />
                <i>
                  - This means that the allowance given to you by your employer
                  is considered a sufficient contribution toward a health plan
                  costing less than or equal to 9.12% of your income.
                </i>
              </p>
            </div>
          }
        ></Alert>
      )}

      {showUnaffordable && (
        <Alert
          className="mt-32 mb-32 font-large-text"
          type="error"
          showIcon={false}
          message=""
          description={
            <div>
              <h3>NO</h3>
              <p>
                Your allowance is unaffordable based on the your income, the
                lowest cost silver plan available to you of ${lowestCostPlan}{' '}
                and your monthly allowance of $
                {showSingleAllowance
                  ? formInitialValues?.singleAllowance
                  : formInitialValues?.monthlyAllowance}
                .<br />
                <br />
                What does this mean?
                <br />
                <br />
                <i>
                  - To be considered affordable by the IRS, the cost of health
                  insurance for an employee must not be more than 9.12% of the
                  employee’s income after taking into account their ICHRA
                  allowance.
                </i>
                <br />
                <i>
                  - The allowance given to you by your employer does not meet
                  the contribution level necessary to be considered affordable.
                </i>
                <br />
                <i>
                  - Follow the next steps to see how this impacts you and what
                  your options are.
                </i>
              </p>
            </div>
          }
        ></Alert>
      )}
    </div>
  );
};
export default Step2;
